/**
 * Live Session Event Debugger
 * 
 * A debug component that subscribes to live session events
 * and displays them in the console and optionally in the UI.
 * This is useful for testing and debugging the event system.
 */

import React, { useState, useEffect } from 'react';
import liveSessionEvents from '../services/liveSessionEvents';

const LiveSessionEventDebugger = ({ showInUI = false }) => {
    const [events, setEvents] = useState([]);
    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        if (!isEnabled) return;

        // Event handlers
        const handleSessionStart = (event) => {
            console.log('🎬 Session Started:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        const handlePhaseChange = (event) => {
            console.log('🔄 Phase Changed:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        const handleTimerTick = (event) => {
            // Log all timer events (now only fires on minute boundaries or completion)
            console.log('⏰ Timer Tick:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        const handlePhotoAdded = (event) => {
            console.log('📸 Photo Added:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        const handleNoteAdded = (event) => {
            console.log('📝 Note Added:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        const handleSessionEnd = (event) => {
            console.log('🏁 Session Ended:', event);
            if (showInUI) {
                setEvents(prev => [...prev.slice(-9), { ...event, id: Date.now() }]);
            }
        };

        // Subscribe to all events
        liveSessionEvents.subscribe('session.start', handleSessionStart);
        liveSessionEvents.subscribe('phase.change', handlePhaseChange);
        liveSessionEvents.subscribe('timer.tick', handleTimerTick);
        liveSessionEvents.subscribe('photo.added', handlePhotoAdded);
        liveSessionEvents.subscribe('note.added', handleNoteAdded);
        liveSessionEvents.subscribe('session.end', handleSessionEnd);

        // Cleanup
        return () => {
            liveSessionEvents.unsubscribe('session.start', handleSessionStart);
            liveSessionEvents.unsubscribe('phase.change', handlePhaseChange);
            liveSessionEvents.unsubscribe('timer.tick', handleTimerTick);
            liveSessionEvents.unsubscribe('photo.added', handlePhotoAdded);
            liveSessionEvents.unsubscribe('note.added', handleNoteAdded);
            liveSessionEvents.unsubscribe('session.end', handleSessionEnd);
        };
    }, [isEnabled, showInUI]);

    if (!showInUI) {
        return null; // Don't show any UI when showInUI is false
    }

    return (
        <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            width: '300px',
            maxHeight: '400px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 9999,
            overflow: 'auto'
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0 }}>Live Session Events</h4>
                <button
                    onClick={() => setIsEnabled(!isEnabled)}
                    style={{
                        padding: '4px 8px',
                        backgroundColor: isEnabled ? '#4CAF50' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '10px'
                    }}
                >
                    {isEnabled ? 'ON' : 'OFF'}
                </button>
            </div>
            
            {isEnabled && (
                <div>
                    {events.length === 0 ? (
                        <div style={{ opacity: 0.7 }}>Waiting for events...</div>
                    ) : (
                        <div>
                            {events.map((event, index) => (
                                <div key={event.id || index} style={{ 
                                    marginBottom: '8px', 
                                    padding: '6px', 
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '4px'
                                }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                        {event.type}
                                    </div>
                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>
                                        {new Date(event.timestamp).toLocaleTimeString()}
                                    </div>
                                    {event.data && (
                                        <div style={{ fontSize: '10px', marginTop: '2px' }}>
                                            {JSON.stringify(event.data, null, 2)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LiveSessionEventDebugger;
